import _ from 'lodash';
import { showSnackbar } from '../notification';
import { makeRequest } from './index';
import { getCreationSource } from '@app/helpers/creation-source';
import { randomUUID } from '@app/utils/uuid';
import i18n from '@app/i18n';

/** @constant {object} */
// if there is a difference in how we are defining the entities in our redux actions and how they need to be represented in our API calls then it should be defined here in this object
// usually the mapping is just adding camelcase to multiple words that make up the entity name
// key = what the entity is called in redux action
// value = what the entity should be represented as in the API calls
const entityMap = {
  AUTOREPLIES: 'autoReplies',
  ADMINASSISTS: 'adminAssists',
};

export const apiRequest = {
  get: {
    chat: {
      scheduledMessages: (chatId) =>
        makeRequest({
          endpoint: `/api/chats/${chatId}/scheduled-messages`,
          method: 'GET',
          includeCreationSource: false,
        }),
    },
  },
  create: {
    message: {
      queue: ({ chatId, jobId, message, eventTime, messageTemplateId }) => {
        const body = {
          chatId,
          jobId,
          eventTime,
          messageBody: message,
          messageId: randomUUID(),
          creationSource: getCreationSource(),
          chatServerUrl: `api/chats/${chatId}/messages`,
        };
        if (messageTemplateId) {
          body.messageTemplateId = messageTemplateId;
        }

        return makeRequest({
          endpoint: `/api/chats/${chatId}/scheduled-messages`,
          method: 'POST',
          body,
          includeCreationSource: false,
        });
      },
    },
  },
  update: {},
  delete: {
    message: {
      queued: ({ chatId, messageId }) =>
        makeRequest({
          endpoint: `/api/chats/${chatId}/scheduled-messages/${messageId}`,
          method: 'DELETE',
          includeCreationSource: false,
        }),
    },
  },
};

/**
 * Builds the api endpoint from the entity names and ids
 * only works with APIs that have been created in a standardized REST format
 *
 * @param {string} primaryEntityName 'name of the primary entity'
 * @param {string} secondaryEntityName  'name of the secondary entity, if one is present'
 * @param {Array} entityIds  'contains all the ids for the entities involved, if we need them'
 *
 * @returns {string} endpoint
 */
const buildEndpoint = (primaryEntityName, secondaryEntityName, entityIds) => {
  let endpoint = `${primaryEntityName}`;

  if (entityIds && entityIds.length > 0) {
    endpoint = `${endpoint}/${entityIds[0]}`;

    if (secondaryEntityName && secondaryEntityName.length > 0) {
      endpoint = `${endpoint}/${secondaryEntityName}`;
      if (entityIds && entityIds.length > 1) {
        endpoint = `${endpoint}/${entityIds[1]}`;
      }
    }
  }
  return endpoint;
};
/**
 * Receives the data needed from the frontend component required to make an API call,
 * makes the API call and
 * creates all the redux REQUEST actions needed to track the lifecycle of the API call
 *
 * @param {string} actionType 'strictly formatted to contain api method type and entities to access'
 * @param {Array} entityIds  'contains all the ids for the entities involved, if we need them'
 * @param {object} data  'the data that we need to modify in some way, if at all'
 *
 * @returns dispatchRequest
 */
export const makeApiRequest = (
  actionType,
  entityIds = [],
  data = null,
  snackBarSuccessMessage = '',
) => {
  // the format of the actionType must start with the api method, then the names of the entities
  // e.g. GET_USERS_AUTOREPLIES, method = GET, primaryEntityName = USERS, secondaryEntityName = AUTOREPLIES
  const [method, primaryEntityName, secondaryEntityName] =
    actionType.split('_');
  const primaryEntityApiName = _.get(
    entityMap,
    primaryEntityName,
    primaryEntityName.toLowerCase(),
  );
  const secondaryEntityApiName = _.get(
    entityMap,
    secondaryEntityName,
    secondaryEntityName.toLowerCase(),
  );
  const endpoint = buildEndpoint(
    primaryEntityApiName,
    secondaryEntityApiName,
    entityIds,
  );
  const options = {
    endpoint,
    method,
  };
  options.endpoint = `/api/${options.endpoint}`;
  if (data) {
    options.body = data;
  }

  return dispatchRequest(makeRequest(options), {
    type: actionType,
    snackBarSuccessMessage,
    entityIds,
  });
};

export const dispatchRequest =
  (
    madeRequestCall,
    {
      type,
      processPayload,
      successCallback,
      snackBarSuccessMessage,
      entityIds,
    },
  ) =>
  (dispatch, getStore) => {
    dispatch({
      type: `REQUEST_${type}_INIT`,
    });
    // Resolves to a success boolean rather than rejecting, so fire-and-forget callers don't leak unhandled rejections.
    return madeRequestCall
      .then((res) => {
        dispatch({
          type,
          payload: processPayload ? processPayload(res.data) : res.data,
          ...(entityIds && entityIds?.length > 0 && { meta: { entityIds } }),
        });

        dispatch({
          type: `REQUEST_${type}_SUCCESS`,
        });

        if (successCallback) {
          dispatch(successCallback(res.data));
        }
        if (snackBarSuccessMessage) {
          dispatch(
            showSnackbar({ text: snackBarSuccessMessage, isError: false }),
          );
        }
        return true;
      })
      .catch((err) => {
        dispatch({
          type: `REQUEST_${type}_ERROR`,
          payload: err,
        });
        const errorKey =
          (getStore()?.ui?.errors || {})[`REQUEST_${type}_ERROR_MSG`] ||
          'common:somethingWentWrong';
        dispatch(showSnackbar({ text: i18n.t(errorKey), isError: true }));
        return false;
      });
  };
